'use client';

import type { Dispatch, SetStateAction } from 'react';
import type { i18n as I18n, TFunction } from 'i18next';
import type { NaceCode } from './searchSidebarOptions';
import { RegisterFilterGroup } from './filter-groups/RegisterFilterGroup';
import { PeopleFilterGroup } from './filter-groups/PeopleFilterGroup';
import { SizeFilterGroup } from './filter-groups/SizeFilterGroup';
import { IndustryFilterGroup } from './filter-groups/IndustryFilterGroup';
import { LocationFilterGroup } from './filter-groups/LocationFilterGroup';
import { ProfileFilterGroup } from './filter-groups/ProfileFilterGroup';
import { KeywordSearchField } from './filter-groups/KeywordSearchField';
import { getAgeRangeLabel, removeSet, toggleSet } from './searchSidebarHelpers';
import type { useFilterPickerState, useSearchFacets, useSidebarOptions } from './useSidebarPickers';
import type { countActiveFilters, SidebarFilterValues, useFilterGroupClears } from './useSidebarFilterState';
import type { useSidebarKeywordSearch } from './useSidebarKeywordSearch';

// CompanySearchSidebar's JSX wiring: each filter group gets its props from the
// sidebar's hooks, passed down as one context object. State stays in the
// sidebar's hooks; these components only route it — the staged filters as
// one `staged` object plus its `set.<key>` setters.

type SidebarKeywordSearchState = ReturnType<typeof useSidebarKeywordSearch>;

export interface SidebarGroupsContext {
  t: TFunction<'translation', undefined>;
  i18n: I18n;
  values: SidebarFilterValues;
  pickers: ReturnType<typeof useFilterPickerState>;
  options: ReturnType<typeof useSidebarOptions>;
  facets: ReturnType<typeof useSearchFacets>;
  counts: ReturnType<typeof countActiveFilters>;
  clears: ReturnType<typeof useFilterGroupClears>;
  handleFormSubmit: SidebarKeywordSearchState['handleFormSubmit'];
  naceCodes: NaceCode[];
  cityList: string[];
  tagList: string[];
  hideNaceCodes: boolean;
  hidePersonSearch: boolean;
  showFinancialYear: boolean;
  onCountryChange?: (country: string) => void;
}

interface SidebarKeywordSearchProps {
  t: TFunction<'translation', undefined>;
  keyword: SidebarKeywordSearchState;
  searchInput: string;
  setSearchInput: Dispatch<SetStateAction<string>>;
}

/**
 * Top-level unified keyword input — binds to `q` (the backend's unified
 * keyword field). Funnels into eu_id → company-name → person-name resolution
 * server-side. Autocomplete dropdown only shows company suggestions per
 * /api/companies/suggest; person / euId terms are submitted via Enter.
 */
export function SidebarKeywordSearch({ t, keyword, searchInput, setSearchInput }: SidebarKeywordSearchProps) {
  const {
    autocompleteRef, setShowLastSearches, fetchSuggestions, setShowSuggestions, fetchLastSearches,
    showSuggestions, suggestions, keyboardNavRef, setSidebarActiveIndex, sidebarActiveIndex,
    handleSuggestionClick, handleFormSubmit, showLastSearches, lastSearches, suggestionsLoading,
    discardLastSearch,
  } = keyword;
  return (
    <KeywordSearchField
      autocompleteRef={autocompleteRef}
      t={t}
      searchInput={searchInput}
      setSearchInput={setSearchInput}
      setShowLastSearches={setShowLastSearches}
      fetchSuggestions={fetchSuggestions}
      setShowSuggestions={setShowSuggestions}
      fetchLastSearches={fetchLastSearches}
      showSuggestions={showSuggestions}
      suggestions={suggestions}
      keyboardNavRef={keyboardNavRef}
      setSidebarActiveIndex={setSidebarActiveIndex}
      sidebarActiveIndex={sidebarActiveIndex}
      handleSuggestionClick={handleSuggestionClick}
      handleFormSubmit={handleFormSubmit}
      showLastSearches={showLastSearches}
      lastSearches={lastSearches}
      suggestionsLoading={suggestionsLoading}
      discardLastSearch={discardLastSearch}
    />
  );
}

/**
 * The six filter groups, in sidebar order. Each gets the whole staged filter
 * state (`staged` / `set`), so a new filter needs no wiring here — only its
 * control in the group's *Parts.tsx.
 */
export function SidebarFilterGroups({ ctx }: { ctx: SidebarGroupsContext }) {
  return (
    <>
      {/* Group 1 — Company profile: Rechtsform, founding window,
          memberships, contactability. First and open by default: it is
          the group a visitor can use in full without an account. */}
      <ProfileGroup ctx={ctx} />

      {/* Group 2 — Location: City + Bundesland */}
      <LocationGroup ctx={ctx} />

      {/* Group 3 — Industry & activity: NACE, Industry, Tags, CPV */}
      <IndustryGroup ctx={ctx} />

      {/* Group 4 — Size & financials: Revenue, Bilanzsumme, Profit,
          Employees, filing year, filings-only */}
      <SizeGroup ctx={ctx} />

      {/* Group 5 — People: person name + avg board birth-year age */}
      {!ctx.hidePersonSearch && <PeopleGroup ctx={ctx} />}

      {/* Group 6 — Register & identifiers: euId, registerId, registerCourt,
          registerType. The unified keyword input (q) sits above all groups
          at the top of the sidebar — see SidebarKeywordSearch. */}
      <RegisterGroup ctx={ctx} />
    </>
  );
}

/** Group 1 — Company profile: Rechtsform, founding window, memberships, contactability. */
function ProfileGroup({ ctx }: { ctx: SidebarGroupsContext }) {
  const { t, handleFormSubmit } = ctx;
  const { staged, set } = ctx.values;
  const {
    openFilterGroup, handleGroupOpenChange, rechtsformOpen, setRechtsformOpen, membershipsOpen,
    setMembershipsOpen,
  } = ctx.pickers;
  const { groupLock, rechtsformOptions, lockOf, membershipOptions } = ctx.options;
  const { facetSum, facetCount } = ctx.facets;
  return (
    <ProfileFilterGroup
      t={t}
      profileCount={ctx.counts.profile}
      clearProfile={ctx.clears.profile}
      openFilterGroup={openFilterGroup}
      handleGroupOpenChange={handleGroupOpenChange}
      groupLock={groupLock}
      staged={staged}
      set={set}
      rechtsformOpen={rechtsformOpen}
      setRechtsformOpen={setRechtsformOpen}
      removeSet={removeSet}
      rechtsformOptions={rechtsformOptions}
      toggleSet={toggleSet}
      facetSum={facetSum}
      handleFormSubmit={handleFormSubmit}
      lockOf={lockOf}
      membershipsOpen={membershipsOpen}
      setMembershipsOpen={setMembershipsOpen}
      membershipOptions={membershipOptions}
      facetCount={facetCount}
    />
  );
}

/** Group 2 — Location: City + Bundesland. */
function LocationGroup({ ctx }: { ctx: SidebarGroupsContext }) {
  const { t, cityList, onCountryChange, i18n } = ctx;
  const { staged, set } = ctx.values;
  const {
    openFilterGroup, handleGroupOpenChange, cityOpen, setCityOpen, setCitySearch, citySearch,
    bundeslandOpen, setBundeslandOpen,
  } = ctx.pickers;
  const { groupLock, lockOf, locationOptions } = ctx.options;
  const { facetCount } = ctx.facets;
  return (
    <LocationFilterGroup
      t={t}
      locationCount={ctx.counts.location}
      clearLocation={ctx.clears.location}
      openFilterGroup={openFilterGroup}
      handleGroupOpenChange={handleGroupOpenChange}
      groupLock={groupLock}
      staged={staged}
      set={set}
      lockOf={lockOf}
      cityOpen={cityOpen}
      setCityOpen={setCityOpen}
      setCitySearch={setCitySearch}
      removeSet={removeSet}
      citySearch={citySearch}
      cityList={cityList}
      toggleSet={toggleSet}
      facetCount={facetCount}
      onCountryChange={onCountryChange}
      bundeslandOpen={bundeslandOpen}
      setBundeslandOpen={setBundeslandOpen}
      i18n={i18n}
      locationOptions={locationOptions}
    />
  );
}

/** Group 3 — Industry & activity: NACE, Industry, Tags, CPV. */
function IndustryGroup({ ctx }: { ctx: SidebarGroupsContext }) {
  const { t, hideNaceCodes, naceCodes, tagList } = ctx;
  const { staged, set, toggleIndustry } = ctx.values;
  const {
    openFilterGroup, handleGroupOpenChange, industryOpen, setIndustryOpen, naceCodesOpen,
    setNaceCodesOpen, setNaceSearch, naceSearch, naceListRef, expandedNaceNodes,
    toggleNaceExpanded, tagsOpen, setTagsOpen, setTagSearch, tagSearch, cpvCodesOpen,
    setCpvCodesOpen, setCpvSearch, cpvCodesList, cpvSearch, cpvListRef,
  } = ctx.pickers;
  const {
    groupLock, lockOf, allIndustries, filteredNaceCodes, naceCodesHaveChildren,
  } = ctx.options;
  const { facetCount } = ctx.facets;
  return (
    <IndustryFilterGroup
      t={t}
      industryCount={ctx.counts.industry}
      clearIndustry={ctx.clears.industry}
      openFilterGroup={openFilterGroup}
      handleGroupOpenChange={handleGroupOpenChange}
      groupLock={groupLock}
      staged={staged}
      set={set}
      lockOf={lockOf}
      industryOpen={industryOpen}
      setIndustryOpen={setIndustryOpen}
      allIndustries={allIndustries}
      toggleIndustry={toggleIndustry}
      facetCount={facetCount}
      hideNaceCodes={hideNaceCodes}
      naceCodesOpen={naceCodesOpen}
      setNaceCodesOpen={setNaceCodesOpen}
      setNaceSearch={setNaceSearch}
      naceCodes={naceCodes}
      removeSet={removeSet}
      naceSearch={naceSearch}
      naceListRef={naceListRef}
      filteredNaceCodes={filteredNaceCodes}
      naceCodesHaveChildren={naceCodesHaveChildren}
      expandedNaceNodes={expandedNaceNodes}
      toggleSet={toggleSet}
      toggleNaceExpanded={toggleNaceExpanded}
      tagsOpen={tagsOpen}
      setTagsOpen={setTagsOpen}
      setTagSearch={setTagSearch}
      tagSearch={tagSearch}
      tagList={tagList}
      cpvCodesOpen={cpvCodesOpen}
      setCpvCodesOpen={setCpvCodesOpen}
      setCpvSearch={setCpvSearch}
      cpvCodesList={cpvCodesList}
      cpvSearch={cpvSearch}
      cpvListRef={cpvListRef}
    />
  );
}

/** Group 4 — Size & financials: Revenue, Bilanzsumme, Profit, Employees, filing year, filings-only. */
function SizeGroup({ ctx }: { ctx: SidebarGroupsContext }) {
  const { t, showFinancialYear } = ctx;
  const { staged, set } = ctx.values;
  const { openFilterGroup, handleGroupOpenChange, yearPickerOpen, setYearPickerOpen } = ctx.pickers;
  const { groupLock, lockOf, yearOptions } = ctx.options;
  const { extents, facetCount } = ctx.facets;
  return (
    <SizeFilterGroup
      t={t}
      sizeCount={ctx.counts.size}
      clearSize={ctx.clears.size}
      openFilterGroup={openFilterGroup}
      handleGroupOpenChange={handleGroupOpenChange}
      groupLock={groupLock}
      staged={staged}
      set={set}
      lockOf={lockOf}
      extents={extents}
      showFinancialYear={showFinancialYear}
      yearPickerOpen={yearPickerOpen}
      setYearPickerOpen={setYearPickerOpen}
      yearOptions={yearOptions}
      facetCount={facetCount}
    />
  );
}

/** Group 5 — People: person name + avg board birth-year age. */
function PeopleGroup({ ctx }: { ctx: SidebarGroupsContext }) {
  const { t, handleFormSubmit } = ctx;
  const { staged, set } = ctx.values;
  const { openFilterGroup, handleGroupOpenChange, ageOpen, setAgeOpen } = ctx.pickers;
  const { groupLock, lockOf } = ctx.options;
  const { facets, facetCount } = ctx.facets;
  return (
    <PeopleFilterGroup
      t={t}
      peopleCount={ctx.counts.people}
      clearPeople={ctx.clears.people}
      openFilterGroup={openFilterGroup}
      handleGroupOpenChange={handleGroupOpenChange}
      groupLock={groupLock}
      staged={staged}
      set={set}
      lockOf={lockOf}
      handleFormSubmit={handleFormSubmit}
      ageOpen={ageOpen}
      setAgeOpen={setAgeOpen}
      getAgeRangeLabel={getAgeRangeLabel}
      toggleSet={toggleSet}
      facets={facets}
      facetCount={facetCount}
    />
  );
}

/** Group 6 — Register & identifiers: euId, registerId, registerCourt, registerType, LEI, USt-IdNr. */
function RegisterGroup({ ctx }: { ctx: SidebarGroupsContext }) {
  const { t, handleFormSubmit, i18n } = ctx;
  const { staged, set } = ctx.values;
  const {
    openFilterGroup, handleGroupOpenChange, registerCourtOpen, setRegisterCourtOpen,
    setRegisterCourtSearch, registerCourtSearch, courtList, registerTypeOpen, setRegisterTypeOpen,
  } = ctx.pickers;
  const { groupLock, registerTypeOptions, lockOf } = ctx.options;
  const { facetCount } = ctx.facets;
  return (
    <RegisterFilterGroup
      t={t}
      registerCount={ctx.counts.register}
      clearRegister={ctx.clears.register}
      openFilterGroup={openFilterGroup}
      handleGroupOpenChange={handleGroupOpenChange}
      groupLock={groupLock}
      staged={staged}
      set={set}
      handleFormSubmit={handleFormSubmit}
      registerCourtOpen={registerCourtOpen}
      setRegisterCourtOpen={setRegisterCourtOpen}
      setRegisterCourtSearch={setRegisterCourtSearch}
      i18n={i18n}
      registerCourtSearch={registerCourtSearch}
      courtList={courtList}
      facetCount={facetCount}
      registerTypeOpen={registerTypeOpen}
      setRegisterTypeOpen={setRegisterTypeOpen}
      registerTypeOptions={registerTypeOptions}
      lockOf={lockOf}
    />
  );
}
